import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store';

import Avatar from '../components/common/Avatar';
import CommonHeader from '../components/layout/CommonHeader';
import Modal from '../components/common/Modal';
import ProfileEditModalContent from '../components/common/ProfileEditModalContent';

import { useUpdateProfile, useUploadProfilePhoto } from '../features/user';
import { toast } from 'react-toastify';
import { setUser } from '@/features/auth/authSlice';
import { useApolloClient } from '@apollo/client';

const EditProfile: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const apolloClient = useApolloClient();
  const { user } = useAppSelector((state) => state.auth);
  
  // State to track if the background image failed to load
  const [imageLoadError, setImageLoadError] = useState<boolean>(false);
  

  
  // Modal states
  const [showNameModal, setShowNameModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showBioModal, setShowBioModal] = useState(false);
  
  // Upload states
  const [originalUserState, setOriginalUserState] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  
  // GraphQL hooks for updating profile and uploading photo
  const [updateProfile] = useUpdateProfile();
  const [uploadProfilePhoto, { loading: uploadLoading }] = useUploadProfilePhoto();
  
  // File input reference for photo upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    // Set document title
    if (user?.name) {
      document.title = `Edit ${user.name} - zapOdeon`;
    } else {
      document.title = 'Edit Profile - zapOdeon';
    }
  }, [user?.name]);

  // If no user is logged in, redirect to login
  if (!user) {
    navigate('/');
    return null;
  }

  // Handle save functions for each field
  const handleSaveName = async (newName: string) => {
    try {
      const result = await updateProfile({
        variables: {
          input: { name: newName }
        }
      });
      
      if (result.data?.updateProfile.success) {
        toast.success(result.data.updateProfile.message || 'Name updated successfully');
        
        // Update Redux store with new user data
        if (result.data.updateProfile.user && user) {
          dispatch(setUser({
            ...user,
            name: result.data.updateProfile.user.name,
            ...(result.data.updateProfile.user.nickname && { nickname: result.data.updateProfile.user.nickname }),
            ...(result.data.updateProfile.user.bio && { bio: result.data.updateProfile.user.bio }),
          } as any));
        }
      }
    } catch (error) {
      console.error('Error updating name:', error);
    }
  };

  const handleSaveUsername = async (newUsername: string) => {
    try {
      const result = await updateProfile({
        variables: {
          input: { nickname: newUsername }
        }
      });
      
      if (result.data?.updateProfile.success) {
        toast.success(result.data.updateProfile.message || 'Username updated successfully');
        
        // Update Redux store with new user data
        if (result.data.updateProfile.user && user) {
          dispatch(setUser({
            ...user,
            name: result.data.updateProfile.user.name,
            ...(result.data.updateProfile.user.nickname && { nickname: result.data.updateProfile.user.nickname }),
            ...(result.data.updateProfile.user.bio && { bio: result.data.updateProfile.user.bio }),
          } as any));
        }
      }
    } catch (error) {
      console.error('Error updating username:', error);
    }
  };

  const handleSaveBio = async (newBio: string) => {
    try {
      const result = await updateProfile({
        variables: {
          input: { bio: newBio }
        }
      });
      
      if (result.data?.updateProfile.success) {
        toast.success(result.data.updateProfile.message || 'Bio updated successfully');
        
        // Update Redux store with new user data
        if (result.data.updateProfile.user && user) {
          dispatch(setUser({
            ...user,
            name: result.data.updateProfile.user.name,
            ...(result.data.updateProfile.user.nickname && { nickname: result.data.updateProfile.user.nickname }),
            ...(result.data.updateProfile.user.bio && { bio: result.data.updateProfile.user.bio }),
          } as any));
        }
      }
    } catch (error) {
      console.error('Error updating bio:', error);
    }
  };

  // Handle photo upload with validation
  const handlePhotoUpload = async (file: File) => {
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, WebP, or HEIC.');
      return;
    }

    // Store original state for potential rollback
    setOriginalUserState(user);
    setUploadProgress(0);

    try {
      // Simulate upload progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      const result = await uploadProfilePhoto(file);
      
      // Complete progress
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (result.data?.uploadProfilePhoto.success) {
        toast.success(result.data.uploadProfilePhoto.message || 'Photo updated successfully');
        

        
        // Update Redux store with complete new user data
        if (result.data.uploadProfilePhoto.user) {
          // Get the fresh user data from the server response
          const serverUser = result.data.uploadProfilePhoto.user;
          
          const updatedUser = {
            // Preserve essential fields from current user
            id: user?.id || serverUser.id,
            email: user?.email || '', // Preserve email as server doesn't return it
            // Update with server response
            name: serverUser.name,
            nickname: serverUser.nickname,
            bio: serverUser.bio,
            followers_count: serverUser.followers_count,
            following_count: serverUser.following_count,
            applauses_count: serverUser.applauses_count,
            is_me: serverUser.is_me,
            // Update photo URLs with server response - create new object to ensure React detects change
            profile_photo_urls: {
              original: serverUser.profile_photo_urls?.original,
              thumbnail: serverUser.profile_photo_urls?.thumbnail,
              small: serverUser.profile_photo_urls?.small,
              medium: serverUser.profile_photo_urls?.medium,
              large: serverUser.profile_photo_urls?.large
            }
          };
          
          dispatch(setUser(updatedUser as any));
          
          // Reset image load error to ensure new photo is displayed
          setImageLoadError(false);
          
          // Clear Apollo cache to ensure fresh data on next queries
          apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'me' });
          apolloClient.cache.gc();
        }
        
        // Reset states
        setOriginalUserState(null);
        setUploadProgress(0);
        
      } else {
        // Handle upload failure
        const errorMessage = result.data?.uploadProfilePhoto.message || 'Upload failed';
        toast.error(`Upload failed: ${errorMessage}`);
        
        // Reset to original state
        if (originalUserState) {
          dispatch(setUser(originalUserState));
        }
        setOriginalUserState(null);
        setUploadProgress(0);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      
      // Reset to original state on error
      if (originalUserState) {
        dispatch(setUser(originalUserState));
        toast.error('Failed to upload photo. Please try again.');
      }
      
      setOriginalUserState(null);
      setUploadProgress(0);
    }
  };

  // Handle photo selection trigger
  const handleChangePhotoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="h-dvh bg-black text-white overflow-hidden">
      {/* Header */}
      <CommonHeader
        title="Edit Profile"
        onBackClick={() => navigate(-1)}
      />
  
      {/* Photo Block - 60% height */}
      <div 
        className="relative w-full"
        style={{ 
          height: '60vh',
          minHeight: '60vh'
        }}
      >
        {/* Background Image or Avatar Fallback */}
        {(((user as any).profile_photo_urls?.large || (user as any).profile_photo_urls?.medium || (user as any).profile_photo_urls?.original) && !imageLoadError) ? (
          <>
            {/* Background Image */}
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ 
                backgroundImage: `url(${(() => {
                  const url = (user as any).profile_photo_urls?.large || (user as any).profile_photo_urls?.medium || (user as any).profile_photo_urls?.original;
                  // Add cache busting to force browser to reload new image
                  return url ? `${url}?t=${Date.now()}` : url;
                })()})` 
              }}
            />
            {/* Note: Removed hidden img element that was causing premature error states */}
            {/* Gradient Overlay */}
            <div 
              className="absolute inset-0 w-full h-full"
              style={{
                background: 'linear-gradient(180deg, rgba(21, 21, 21, 0.50) 0%, rgba(21, 21, 21, 0.00) 22.57%, #151515 94.06%)'
              }}
            />
          </>
        ) : (
          <>
            {/* Avatar Fallback with Background */}
            <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-gray-800 to-black flex items-center justify-center">
              <Avatar
                name={user.name}
                profilePhotoUrls={(user as any).profile_photo_urls}
                shape="square"
                className="w-full h-full"
                style={{ maxWidth: '100vw', maxHeight: '60vh' }}
              />
            </div>
            {/* Gradient Overlay for consistency */}
            <div 
              className="absolute inset-0 w-full h-full"
              style={{
                background: 'linear-gradient(180deg, rgba(21, 21, 21, 0.30) 0%, rgba(21, 21, 21, 0.00) 22.57%, #151515 94.06%)'
              }}
            />
          </>
        )}

        {/* Upload Progress Overlay */}
        {uploadLoading && (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center z-20">
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            
            {/* Upload progress content */}
            <div className="relative z-10 text-center text-white">
              {/* Animated upload icon */}
              <div className="mb-4 flex justify-center">
                <div className="relative">
                  <svg 
                    className="w-16 h-16 animate-pulse" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M12 2L12 8M12 8L9 5M12 8L15 5M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                  {/* Progress ring */}
                  <svg className="absolute inset-0 w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="4"
                      fill="none"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      stroke="url(#gradient)"
                      strokeWidth="4"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${uploadProgress * 1.76} 176`}
                      className="transition-all duration-300"
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#FF2A2E" />
                        <stop offset="100%" stopColor="#FEB03A" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
              
              {/* Upload text and progress */}
              <div className="space-y-2">
                <p className="text-lg font-semibold">Uploading photo...</p>
                <p className="text-sm text-white/70">{Math.round(uploadProgress)}% complete</p>
                <div className="w-48 bg-white/20 rounded-full h-1 mx-auto">
                  <div 
                    className="h-1 rounded-full transition-all duration-300"
                    style={{
                      width: `${uploadProgress}%`,
                      background: 'linear-gradient(90deg, #FF2A2E 0%, #FEB03A 100%)'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        

      </div>

      {/* Content Section - 40% height */}
      <div className="flex-1 bg-black absolute bottom-[10px] left-0 right-0 bg-black" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
        {/* Fixed Edit Buttons at bottom */}
        <div 
          className="relative"
          
        >
          {/* Edit Buttons */}
          <div>
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handlePhotoUpload(file);
              }
            }}
            disabled={uploadLoading}
            className="hidden"
          />
          
          {/* Change Photo Button */}
          <button 
            onClick={handleChangePhotoClick}
            disabled={uploadLoading}
            className="flex w-full items-center justify-center"
            style={{ 
              padding: '20px',
              background: 'transparent',
              color: '#FFF',
              fontFamily: '"Aeonik TRIAL"',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '-0.2px',
              border: 'none',
              borderRadius: '16px',
              marginBottom: '6px'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '12px' }}>
              <path fillRule="evenodd" clipRule="evenodd" d="M8.14866 17.5H11.852C14.4528 17.5 15.7537 17.5 16.6878 16.8875C17.091 16.6235 17.4382 16.2825 17.7095 15.8842C18.3337 14.9675 18.3337 13.69 18.3337 11.1367C18.3337 8.58337 18.3337 7.30587 17.7095 6.38921C17.4382 5.99087 17.091 5.64992 16.6878 5.38587C16.0878 4.99171 15.3362 4.85087 14.1853 4.80087C13.6362 4.80087 13.1637 4.39254 13.0562 3.86337C12.974 3.47576 12.7605 3.1284 12.4519 2.88C12.1432 2.63159 11.7582 2.49738 11.362 2.50004H8.63866C7.81533 2.50004 7.10616 3.07087 6.94449 3.86337C6.83699 4.39254 6.36449 4.80087 5.81533 4.80087C4.66533 4.85087 3.91366 4.99254 3.31283 5.38587C2.90994 5.65001 2.56305 5.99095 2.29199 6.38921C1.66699 7.30587 1.66699 8.58254 1.66699 11.1367C1.66699 13.6909 1.66699 14.9667 2.29116 15.8842C2.56116 16.2809 2.90783 16.6217 3.31283 16.8875C4.24699 17.5 5.54783 17.5 8.14866 17.5ZM10.0003 7.72754C8.08283 7.72754 6.52783 9.25337 6.52783 11.1359C6.52783 13.0184 8.08366 14.5467 10.0003 14.5467C11.917 14.5467 13.4728 13.02 13.4728 11.1375C13.4728 9.25504 11.917 7.72754 10.0003 7.72754ZM10.0003 9.09087C8.85033 9.09087 7.91699 10.0067 7.91699 11.1367C7.91699 12.2659 8.85033 13.1817 10.0003 13.1817C11.1503 13.1817 12.0837 12.2659 12.0837 11.1367C12.0837 10.0075 11.1503 9.09087 10.0003 9.09087ZM13.9353 8.4092C13.9353 8.03254 14.2462 7.72754 14.6303 7.72754H15.5553C15.9387 7.72754 16.2503 8.03254 16.2503 8.4092C16.2486 8.59162 16.1745 8.76588 16.0443 8.89369C15.9141 9.02151 15.7386 9.09243 15.5562 9.09087H14.6303C14.5399 9.09175 14.4502 9.07482 14.3664 9.04104C14.2825 9.00725 14.2062 8.95729 14.1416 8.89399C14.0771 8.83069 14.0257 8.7553 13.9903 8.67212C13.9549 8.58894 13.9362 8.4996 13.9353 8.4092Z" fill="#FF302E"/>
            </svg>
            <span style={{
              color: uploadLoading ? '#888' : '#ff2a2e'
            }}>
              {uploadLoading ? 'Uploading...' : 'Change photo'}
            </span>
          </button>

          {/* Separator */}
          <div style={{ 
            marginTop: '24px', 
            marginBottom: '24px', 
            width: '100%',
            height: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.06)'
          }}></div>

          {/* Name Button */}
          <button 
            onClick={() => setShowNameModal(true)}
            className="flex w-full justify-between items-center"
            style={{ 
              padding: '20px',
              background: '#202020',
              color: '#FFF',
              fontFamily: '"Aeonik TRIAL"',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '-0.2px',
              border: 'none',
              borderRadius: '16px',
              marginBottom: '6px'
            }}
          >
            <span>Name</span>
            <div className="flex items-center gap-3">
              <span>{user.name}</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.1962 9.69184L7.67116 4.33351C7.32533 3.99934 6.66699 4.20184 6.66699 4.64184V15.3585C6.66699 15.7985 7.32533 16.001 7.67116 15.6668L13.1962 10.3085C13.2391 10.2695 13.2735 10.2219 13.2969 10.1688C13.3204 10.1157 13.3326 10.0582 13.3326 10.0002C13.3326 9.94211 13.3204 9.88469 13.2969 9.8316C13.2735 9.7785 13.2391 9.7309 13.1962 9.69184Z" fill="white" fillOpacity="0.56"/>
              </svg>
            </div>
          </button>

          {/* Username Button */}
          <button 
            onClick={() => setShowUsernameModal(true)}
            className="flex w-full justify-between items-center"
            style={{ 
              padding: '20px',
              background: '#202020',
              color: '#FFF',
              fontFamily: '"Aeonik TRIAL"',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '-0.2px',
              border: 'none',
              borderRadius: '16px',
              marginBottom: '6px'
            }}
          >
            <span>Username</span>
            <div className="flex items-center gap-3">
              <span>@{(user as any).nickname}</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.1962 9.69184L7.67116 4.33351C7.32533 3.99934 6.66699 4.20184 6.66699 4.64184V15.3585C6.66699 15.7985 7.32533 16.001 7.67116 15.6668L13.1962 10.3085C13.2391 10.2695 13.2735 10.2219 13.2969 10.1688C13.3204 10.1157 13.3326 10.0582 13.3326 10.0002C13.3326 9.94211 13.3204 9.88469 13.2969 9.8316C13.2735 9.7785 13.2391 9.7309 13.1962 9.69184Z" fill="white" fillOpacity="0.56"/>
              </svg>
            </div>
          </button>

          {/* Bio Button */}
          <button 
            onClick={() => setShowBioModal(true)}
            className="flex w-full justify-between items-center"
            style={{ 
              padding: '20px',
              background: '#202020',
              color: '#FFF',
              fontFamily: '"Aeonik TRIAL"',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '-0.2px',
              border: 'none',
              borderRadius: '16px',
              marginBottom: '6px'
            }}
          >
            <span>Bio</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.1962 9.69184L7.67116 4.33351C7.32533 3.99934 6.66699 4.20184 6.66699 4.64184V15.3585C6.66699 15.7985 7.32533 16.001 7.67116 15.6668L13.1962 10.3085C13.2391 10.2695 13.2735 10.2219 13.2969 10.1688C13.3204 10.1157 13.3326 10.0582 13.3326 10.0002C13.3326 9.94211 13.3204 9.88469 13.2969 9.8316C13.2735 9.7785 13.2391 9.7309 13.1962 9.69184Z" fill="white" fillOpacity="0.56"/>
            </svg>
          </button>

          {/* Link Button */}
          <button 
            onClick={() => {/* TODO: Navigate to edit link */}}
            className="flex w-full justify-between items-center"
            style={{ 
              padding: '20px',
              background: '#202020',
              color: '#FFF',
              fontFamily: '"Aeonik TRIAL"',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '-0.2px',
              border: 'none',
              borderRadius: '16px',
              marginBottom: '6px'
            }}
          >
            <span>Link</span>
            <div className="flex items-center gap-3">
              <span>0</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.1962 9.69184L7.67116 4.33351C7.32533 3.99934 6.66699 4.20184 6.66699 4.64184V15.3585C6.66699 15.7985 7.32533 16.001 7.67116 15.6668L13.1962 10.3085C13.2391 10.2695 13.2735 10.2219 13.2969 10.1688C13.3204 10.1157 13.3326 10.0582 13.3326 10.0002C13.3326 9.94211 13.3204 9.88469 13.2969 9.8316C13.2735 9.7785 13.2391 9.7309 13.1962 9.69184Z" fill="white" fillOpacity="0.56"/>
              </svg>
            </div>
          </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={showNameModal}
        onClose={() => setShowNameModal(false)}
        title="Name"
      >
        <ProfileEditModalContent
          onClose={() => setShowNameModal(false)}
          fieldType="text"
          initialValue={user.name || ''}
          onSave={handleSaveName}
          placeholder="Enter your name"
        />
      </Modal>

      <Modal
        isOpen={showUsernameModal}
        onClose={() => setShowUsernameModal(false)}
        title="Username"
      >
        <ProfileEditModalContent
          onClose={() => setShowUsernameModal(false)}
          fieldType="text"
          initialValue={(user as any).nickname || ''}
          onSave={handleSaveUsername}
          placeholder="Enter your username"
        />
      </Modal>

      <Modal
        isOpen={showBioModal}
        onClose={() => setShowBioModal(false)}
        title="Bio"
      >
        <ProfileEditModalContent
          onClose={() => setShowBioModal(false)}
          fieldType="textarea"
          initialValue={(user as any).bio || ''}
          onSave={handleSaveBio}
          placeholder="Write something about yourself..."
        />
      </Modal>
    </div>
  );
};

export default EditProfile; 